import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  WorkplaceRequestCommentsParams, 
  WorkplaceRequestCommentsResponse,
  CreateWorkplaceRequestCommentParams,
  UpdateWorkplaceRequestCommentParams
} from '@gettactic/api';
import { api } from '@/lib/api/api';
import { useAuthenticated } from '@gettactic/hooks';
import { toast } from 'react-hot-toast';

export function useWorkplaceRequestComments(
  ticketId: string,
  params: WorkplaceRequestCommentsParams = {},
  options: {
    enabled?: boolean;
    getNextPageParam?: (
      lastPage: WorkplaceRequestCommentsResponse | null
    ) => string | null;
    keepPreviousData?: boolean;
  } = {}
) {
  const {
    userContext: { deviceTz, authenticatedUser }
  } = useAuthenticated();
  const tz = authenticatedUser?.user?.time_zone;


  const defaultOptions = {
    enabled: true,
    getNextPageParam: (lastPage: WorkplaceRequestCommentsResponse | null) => {
      if (!lastPage?.cursor) {
        return null;
      }
      return lastPage.cursor;
    },
    keepPreviousData: true
  };

  const requestParams = { ...params };
  if (!requestParams.limit) {
    requestParams.limit = 50;
  }

  const {
    data,
    error,
    fetchNextPage,
    refetch,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    status
  } = useInfiniteQuery(
    ['workplaceRequestComments', ticketId, requestParams],
    async ({ pageParam: cursor }) => {
      const queryParams = { ...requestParams, tz: tz ?? deviceTz };
      if (cursor) {
        queryParams.cursor = cursor;
      }
      
      const res = await api.client.workplaceRequestComments.getWorkplaceRequestComments(ticketId, queryParams);
      return res.result;
    },
    {
      ...defaultOptions,
      ...options,
      keepPreviousData: options.keepPreviousData ? options.keepPreviousData : true,
      getNextPageParam: (lastPage) => {
        if (!lastPage?.cursor) {
          return null;
        }

        if (lastPage.elements.length < (requestParams.limit ?? 50)) {
          return null;
        }

        return lastPage.cursor;
      }
    }
  );

  const allElements = data?.pages.flatMap((page) => page?.elements ?? []) ?? [];
  const latestPage = data?.pages[data.pages.length - 1];

  return {
    data,
    error,
    fetchNextPage,
    refetch,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    status,
    allElements,
    totalElements: allElements.length
  };
}

export function useWorkplaceRequestCommentMutations() {
  const queryClient = useQueryClient();
  const {
    userContext: { deviceTz, authenticatedUser }
  } = useAuthenticated();
  const tz = authenticatedUser?.user?.time_zone;

  const updateAll = () => {
    queryClient.invalidateQueries(['workplaceRequestComments']);
    queryClient.invalidateQueries(['workplaceRequests']);
  }

  const createComment = useMutation(
    async ({ ticketId, params }: { ticketId: string; params: CreateWorkplaceRequestCommentParams }) => {
      const queryParams = { ...params, tz: tz ?? deviceTz };
      const res = await api.client.workplaceRequestComments.createWorkplaceRequestComment(ticketId, queryParams);
      return res.result;
    },
    {
      onSuccess: (data, variables) => {
        // Invalidate and refetch comments for this ticket
        queryClient.invalidateQueries(['workplaceRequestComments', variables.ticketId]);
        toast.success('Comment added successfully');
      },
      onError: (error) => {
        console.error('Error creating comment:', error);
        toast.error('Failed to add comment');
      }
    }
  );

  const updateComment = useMutation(
    async ({ 
      ticketId, 
      commentId, 
      params 
    }: { 
      ticketId: string; 
      commentId: string; 
      params: UpdateWorkplaceRequestCommentParams 
    }) => {
      const queryParams = { ...params, tz: tz ?? deviceTz };
      const res = await api.client.workplaceRequestComments.updateWorkplaceRequestComment(ticketId, commentId, queryParams);
      return res.result;
    },
    {
      onSuccess: (data, variables) => {
        // Invalidate and refetch comments for this ticket
        queryClient.invalidateQueries(['workplaceRequestComments', variables.ticketId]);
        toast.success('Comment updated successfully');
      },
      onError: (error) => {
        console.error('Error updating comment:', error);
        toast.error('Failed to update comment');
      }
    }
  );

  const deleteComment = useMutation(
    async ({ ticketId, commentId }: { ticketId: string; commentId: string }) => {
      const res = await api.client.workplaceRequestComments.deleteWorkplaceRequestComment(ticketId, commentId);
      return res.result;
    },
    {
      onSuccess: (data, variables) => {
        // Invalidate and refetch comments for this ticket
        queryClient.invalidateQueries(['workplaceRequestComments', variables.ticketId]);
        toast.success('Comment deleted successfully');
      },
      onError: (error) => {
        console.error('Error deleting comment:', error);
        toast.error('Failed to delete comment');
      }
    }
  );

  return {
    createComment,
    updateComment,
    deleteComment
  };
}
